import PropTypes from "prop-types";
import React from "react";
import { Button, Form, Image } from "react-bootstrap";
import { checkEnvOrUrl, getSearchParam } from "../utils";

const PARTICIPANT_ID_REGEX = /^[\p{L}\p{N}_]{1,64}$/u;
const STUDY_ID_REGEX = /^[\p{L}\p{N}_]{1,24}$/u;
const SESSION_ID_REGEX = /^[\p{L}\p{N}_]+$/u;

// Strip any character that is not a Unicode letter, number, or underscore, then enforce max length
function sanitizeAlphanumUnderscore(value, maxLen) {
  return value.normalize("NFC").replace(/[^\p{L}\p{N}_]/gu, "").slice(0, maxLen);
}

export default function Login({ validationFunction, setMetadataFunction, handleLogin }) {
  // State variables for login screen (Can be initialized from the ENV or URL)
  // Sanitize values from URL/ENV at initialization to prevent reflected injection
  const [studyID, setStudyID] = React.useState(
    () => sanitizeAlphanumUnderscore(checkEnvOrUrl("STUDY_ID") ?? "", 24)
  );
  const [participantID, setParticipantID] = React.useState(
    () => sanitizeAlphanumUnderscore(checkEnvOrUrl("PARTICIPANT_ID") ?? "", 64)
  );
  const [sessionID, setSessionID] = React.useState(
    () => sanitizeAlphanumUnderscore(checkEnvOrUrl("SESSION_ID") ?? "")
  );

  // State variable for handling errors
  const [errorMessage, setErrorMessage] = React.useState("");
  // State variable for handling loading states
  const [isLoading, setIsLoading] = React.useState(false);

  // Initialize login information on the first render
  // TODO: What's the best place for this check?
  React.useEffect(() => {
    // Error if the default Prolific environment variable is found
    if (process.env.REACT_APP_PROLIFIC_PID) {
      // NOTE: This variable comes from Prolific no the ENV - this error will likely never happen
      throw new Error(
        `The environment variable "REACT_APP_PROLIFIC_PID" is not supported, received ${process.env.REACT_APP_PROLIFIC_PID}.\n` +
          `Please change the variable to "REACT_APP_PARTICIPANT_ID"`
      );
    }

    // Error if the default Prolific URL search parameter is found
    if (getSearchParam("PROLIFIC_PID")) {
      throw new Error(
        `The URL search parameter "PROLIFIC_PID" is not supported, received ${getSearchParam("PROLIFIC_PID")}.\n` +
          `Please change the variable to "PARTICIPANT_ID"`
      );
    }
  }, []);

  // Validates and logs in the participant
  function submitLogin() {
    // re-validate sanitized values before sending to Firebase
    // Prevents injection through programmatic state manipulation
    if (!PARTICIPANT_ID_REGEX.test(participantID)) {
      setErrorMessage("Participant ID must be 1–64 letters, numbers, or underscores.");
      return;
    }
    if (!STUDY_ID_REGEX.test(studyID)) {
      setErrorMessage("Study ID must be 1–24 letters, numbers, or underscores.");
      return;
    }
    if (sessionID !== "" && !SESSION_ID_REGEX.test(sessionID)) {
      setErrorMessage("Session ID must contain only letters, numbers, or underscores.");
      return;
    }

    setIsLoading(true);

    // Logs user in if a valid participant/study id combination is given
    validationFunction(studyID, participantID, sessionID).then(async (result) => {
      // Handle validation result - it could be a boolean or an object with isValid and error
      const isValid = result?.isValid ?? result;
      const error = result?.error;
      
      if (!isValid) {
        setIsLoading(false);
        // Check if it's a Firebase permission error
        if (error?.message?.includes("Missing or insufficient permissions")) {
          setErrorMessage("The specified study cannot be found. Is your IDs information correct?");
        } else if (error) {
          // Generic error message for other Firebase errors
          setErrorMessage("Unable to begin the study. Is your login information correct?");
        } else {
          // Fallback for non-Firebase validation
          setErrorMessage("Unable to begin the study. Is your login information correct?");
        }
      } else {
        setErrorMessage("");
        const metadataResult = await setMetadataFunction(studyID);
        
        // Handle metadata result - it could be a boolean (for backward compatibility) or an object with isValid and error
        const metadataIsValid = metadataResult?.isValid ?? metadataResult;
        const metadataError = metadataResult?.error;
        
        if (metadataIsValid) {
          setIsLoading(false);
          handleLogin(studyID, participantID, sessionID);
        } else {
          setIsLoading(false);
          // Display metadata-specific error
          const errorMsg = metadataError || "Unable to load study configuration. Please contact support.";
          setErrorMessage(errorMsg);
        }
      }
    });
  }

  // Form submit handler - delegates to submitLogin after preventing the page reload
  function handleSubmit(e) {
    e.preventDefault();
    submitLogin();
  }

  return (
    <div className="centered-h-v">
      {/* Title Image */}
      <Image
        src="/assets/images/gearshift-title.svg"
        alt="Gearshift title image"
        className="width-50"
      />

      {/* Login Form */}
      <Form onSubmit={handleSubmit} className="width-50 d-grid">
        <Form.Group size="lg" controlId="participantID">
          <Form.Label>Participant ID</Form.Label>
          <Form.Control
            autoFocus
            name="participantID"
            required={true}
            maxLength={64}
            title="1–64 letters, numbers, or underscores"
            value={participantID}
            onChange={(e) => setParticipantID(sanitizeAlphanumUnderscore(e.target.value, 64))}
          />
        </Form.Group>
        <Form.Group size="lg" controlId="studyID">
          <Form.Label>Study ID</Form.Label>
          <Form.Control
            name="studyID"
            required={true}
            maxLength={24}
            title="1–24 letters, numbers, or underscores"
            value={studyID}
            onChange={(e) => setStudyID(sanitizeAlphanumUnderscore(e.target.value, 24))}
          />
        </Form.Group>
        <Form.Group size="lg" controlId="sessionID">
          <Form.Label>Session ID</Form.Label>
          <Form.Control
            name="sessionID"
            title="Letters, numbers, or underscores"
            value={sessionID}
            onChange={(e) => setSessionID(sanitizeAlphanumUnderscore(e.target.value))}
            disabled={true}
          />
        </Form.Group>
        <Button type="submit" variant="secondary" size="lg">
          {isLoading ? "Submitting..." : "Log In"}
        </Button>
        {errorMessage ? (
          <Form.Text className="alert alert-danger">
            {errorMessage}
          </Form.Text>
        ) : null}
      </Form>

      {/* Copyright information */}
      <div>© 2024 Nadja R. Ging-Jehli. All rights reserved.</div>
    </div>
  );
}

Login.propTypes = {
  validationFunction: PropTypes.func,
  setMetadataFunction: PropTypes.func,
  handleLogin: PropTypes.func,
};
